import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearInsights, dropPendingInsights, fetchInsights, insightsKeyServes, insightsRequest, insightsTooLong,
  sameInsightsBase, selectInsightsLoadedKey, selectInsightsLoading,
} from '../../slices/insightsSlice';

// Loads insights for the applied filters: again whenever a full refetch
// starts (`appliedKey`, the applied fetch signature) or insights are turned
// on, and clears them when off. It reads the filters of the render it runs
// in, so turning insights on just before a debounced apply still ends with
// the applied window loaded. Nothing at all happens for users without access,
// nor before any filters are applied, nor on a remount (navigating back) when
// the store already holds insights for the applied key. A ramp-method switch
// or observed ramps turned on keep the current insights on screen until the
// new ones arrive; observed ramps turned off are only hidden (no request).
// Switching back to what is shown drops a load still running for the other key.
export default function useInsightsFetch({ available, on, filters, appliedKey }) {
  const dispatch = useDispatch();
  const loadedKey = useSelector(selectInsightsLoadedKey);
  const loading = useSelector(selectInsightsLoading);
  const lastOn = useRef(on);
  useEffect(() => {
    const onChanged = lastOn.current !== on;
    lastOn.current = on;
    if (!available || appliedKey == null) return;
    if (on && !onChanged && insightsKeyServes(loadedKey, appliedKey)) {
      if (loading) dispatch(dropPendingInsights());
      return;
    }
    const args = insightsRequest({ on, ...filters });
    const tooLong = on && filters.resourceId && filters.startDate && filters.endDate && insightsTooLong(filters);
    if (!(args && !onChanged && sameInsightsBase(loadedKey, appliedKey))) {
      dispatch(clearInsights(tooLong ? 'too_long' : null));
    }
    if (args) dispatch(fetchInsights({ ...args, key: appliedKey }));
  }, [available, on, appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps
}
